import { useState, useEffect, useCallback, useRef } from 'react';
import type { SelectionData } from '../types';

export function useTextSelection(containerRef: React.RefObject<HTMLElement | null>) {
  const [selection, setSelection] = useState<SelectionData | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculateOffsets = useCallback(
    (range: Range): { startOffset: number; endOffset: number } | null => {
      const container = containerRef.current;
      if (!container) return null;

      try {
        // Create a range from the start of the container to the start of the selection
        const preRange = document.createRange();
        preRange.selectNodeContents(container);
        preRange.setEnd(range.startContainer, range.startOffset);
        const startOffset = preRange.toString().length;

        // End offset is start + selection length
        const endOffset = startOffset + range.toString().length;

        return { startOffset, endOffset };
      } catch (e) {
        console.error('Error calculating offsets:', e);
        return null;
      }
    },
    [containerRef]
  );

  const handleSelectionChange = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !containerRef.current) {
      setSelection(null);
      return;
    }

    const range = sel.getRangeAt(0);
    const container = containerRef.current;

    // Check if selection is within our container
    if (!container.contains(range.commonAncestorContainer)) {
      setSelection(null);
      return;
    }

    const text = sel.toString().trim();
    if (text.length < 3) {
      setSelection(null);
      return;
    }

    // Calculate offsets
    const offsets = calculateOffsets(range);
    if (!offsets) {
      setSelection(null);
      return;
    }

    // Get bounding rect for toolbar positioning
    const rect = range.getBoundingClientRect();

    setSelection({
      text,
      startOffset: offsets.startOffset,
      endOffset: offsets.endOffset,
      rect,
    });
  }, [containerRef, calculateOffsets]);

  const clearSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    setSelection(null);
  }, []);

  // Desktop: Listen for selection changes
  useEffect(() => {
    const handleMouseUp = () => {
      // Small delay to ensure selection is complete
      setTimeout(handleSelectionChange, 10);
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleSelectionChange]);

  // Mobile: Handle touch selection
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = () => {
      // Clear any existing selection on new touch
      longPressTimer.current = setTimeout(() => {
        // Haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }, 500);
    };

    const handleTouchEnd = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
      // Delay to allow mobile selection UI to complete
      setTimeout(handleSelectionChange, 150);
    };

    const handleTouchMove = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchmove', handleTouchMove);
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, [containerRef, handleSelectionChange]);

  // Clear selection when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't clear if clicking on highlight toolbar
      if (target.closest('[data-highlight-toolbar]')) {
        return;
      }
      // Clear selection if clicking outside selected text
      if (selection && !target.closest('mark')) {
        clearSelection();
      }
    };

    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [selection, clearSelection]);

  return { selection, clearSelection };
}
